import React, { useCallback, useState } from 'react';
import { AllieSectionsProps, AllieSectionVariant } from './Sections.types';
import {
  SectionButtonContainer,
  SectionButton,
  NumberCircle,
  ContentButton,
} from './Sections.styles';
import { AllieColors } from '../../common/color.types';
import { useSections } from './useSections';
import { AllieText } from '../Text';

const DEFAULT_SECTION_VARIANT: AllieSectionVariant = 'primary';

function getTextColor(variant: AllieSectionVariant | undefined, isSelected: boolean): AllieColors {
  const colorMap: Record<AllieSectionVariant, Record<string, AllieColors>> = {
    primary: {
      true: 'white',
      false: 'neutral-50',
    },
    'primary-soft': {
      true: 'primary-70',
      false: 'neutral-50',
    },
    secondary: {
      true: 'primary-70',
      false: 'neutral-50',
    },
    light: {
      true: 'neutral-90',
      false: 'neutral-50',
    },
  };

  return (colorMap[variant || DEFAULT_SECTION_VARIANT] || colorMap[DEFAULT_SECTION_VARIANT])[
    isSelected.toString()
  ];
}

export const AllieSections: React.FC<AllieSectionsProps> = ({
  sections,
  baseUrl,
  onSectionChange,
  variant = 'primary',
  defaultSection,
  filtersToKeep,
  filters,
  getDeep,
  isMobile = false,
  gap = 'xs',
  useInternalState = false,
  onClickSection,
  direction,
  urlKey,
  textTypeSet,
  style,
  className,
}) => {
  type SectionKey = (typeof sections)[number]['key'];
  const [internalSection, setInternalSection] = useState<SectionKey>(defaultSection);
  const safeVariant = variant || DEFAULT_SECTION_VARIANT;
  const getTextWeight = (isSelected: boolean) => {
    if (safeVariant === 'primary-soft') {
      return isSelected ? 500 : 400;
    }

    return 600;
  };

  const handleInternalSectionChange = useCallback(
    (key: SectionKey) => {
      setInternalSection(key);
      onSectionChange?.(key);
    },
    [onSectionChange]
  );

  let currentSection: SectionKey = internalSection;
  let handleSectionMenu: (sectionKey: SectionKey) => void = handleInternalSectionChange;

  if (!useInternalState) {
    const { section, handleSectionChange } = useSections<SectionKey>({
      sectionKey: urlKey,
      filters,
      filtersToKeep,
      defaultSection,
      basePath: baseUrl,
      getDeep: getDeep as (curr: SectionKey) => number,
      onSectionChange,
    });
    currentSection = section;
    handleSectionMenu = handleSectionChange;
  }

  return (
    <SectionButtonContainer
      variant={safeVariant}
      gap={gap}
      direction={direction}
      style={style}
      className={className}
    >
      {sections.map((section) => {
        const isSelected = section.key === currentSection;
        const digits = section.count !== undefined ? section.count.toString().length : 0;
        const Icon = section.icon;
        return section.hidden ? null : (
          <SectionButton
            key={section.key}
            isSelected={isSelected}
            variant={safeVariant}
            onclick={() => {
              handleSectionMenu(section.key);
              onClickSection?.(section);
            }}
            corner="rounded-edges-plus"
            mode="md"
            allieSize="xs"
            isMobile={isMobile}
            direction={direction}
            data-testid={section.testId}
          >
            <ContentButton>
              <AllieText
                type={isMobile ? 'body3' : 'body2'}
                weight={getTextWeight(isSelected)}
                color={getTextColor(safeVariant, isSelected)}
                typeSet={textTypeSet}
              >
                {section.label}
              </AllieText>
              {section.count && (
                <NumberCircle
                  digits={digits}
                  variant={section.variant || 'primary'}
                  selected={isSelected}
                  alwaysSameColor={section.alwaysSameColor}
                >
                  {section.count}
                </NumberCircle>
              )}
              {Icon && React.isValidElement(<Icon />) && <Icon />}
            </ContentButton>
          </SectionButton>
        );
      })}
    </SectionButtonContainer>
  );
};
